/**
 * ProposalCard Component
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 2: Create ProposalCard component
 *
 * Card showing proposal summary with status badge, vote count, time remaining,
 * and "Already Voted" indicator.
 *
 * @see AC-4.2.8.2 - Given user has voted, when viewing ProposalCard, then "Already Voted" badge is visible
 *
 * @module components/governance/ProposalCard
 */

import { useNavigate } from 'react-router-dom';
import { Clock, Check, FileText, MessageSquare } from 'lucide-react';
import { formatTimeRemaining } from '../../utils/votingHelpers';

// =============================================================================
// Types
// =============================================================================

export type ProposalCardStatus = 'Active' | 'Pending' | 'Closed' | 'Passed' | 'Rejected' | 'Failed';

export interface ProposalCardProps {
  /** Proposal ID */
  id: number;
  /** Proposal title */
  title: string;
  /** Proposal category */
  category: string;
  /** Proposal status */
  status: ProposalCardStatus;
  /** Voting end timestamp (Unix milliseconds) */
  votingEndsAt: number;
  /** Yes votes count */
  yesVotes: number;
  /** No votes count */
  noVotes: number;
  /** Abstain votes count */
  abstainVotes: number;
  /** Needs Refinement votes count */
  needsRefinementVotes: number;
  /** Total vote count */
  totalVotes: number;
  /** Quorum threshold */
  quorumThreshold: number;
  /** Whether current user has voted */
  userHasVoted: boolean;
  /** Comment count for this proposal (BL-024.3 AC3) */
  commentCount?: number;
}

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<ProposalCardStatus, string> = {
  Active: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  Closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  Passed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

// =============================================================================
// Component
// =============================================================================

/**
 * Proposal card component for proposal list
 */
export function ProposalCard({
  id,
  title,
  category,
  status,
  votingEndsAt,
  yesVotes,
  noVotes,
  abstainVotes,
  needsRefinementVotes,
  totalVotes,
  userHasVoted,
  commentCount,
}: ProposalCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/proposals/${id}`);
  };

  const isActive = status === 'Active';
  const timeRemaining = isActive && votingEndsAt > Date.now()
    ? formatTimeRemaining(votingEndsAt)
    : null;

  return (
    <div
      className="bg-card rounded-lg border border-border p-4 hover:border-muted-foreground/50 transition-colors cursor-pointer"
      onClick={handleClick}
      data-testid="proposal-card"
      role="article"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <FileText className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span
                className="px-2 py-0.5 text-xs font-medium rounded bg-primary/10 text-primary"
                data-testid="proposal-card-category"
              >
                {category}
              </span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_COLORS[status]}`}
                data-testid="proposal-card-status"
              >
                {status}
              </span>
              {userHasVoted && (
                <span
                  className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  data-testid="proposal-card-voted-badge"
                >
                  <Check className="w-3 h-3" />
                  Already Voted
                </span>
              )}
            </div>
            <h3
              className="font-medium text-foreground truncate"
              data-testid="proposal-card-title"
            >
              #{id}: {title}
            </h3>
          </div>
        </div>
      </div>

      {/* Meta info */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
        <span data-testid="proposal-card-votes">
          {totalVotes} votes
        </span>
        {timeRemaining && (
          <span className="flex items-center gap-1" data-testid="proposal-card-time">
            <Clock className="w-3.5 h-3.5" />
            {timeRemaining}
          </span>
        )}
        <span className="flex items-center gap-1" data-testid="proposal-card-comment-count">
          <MessageSquare className="w-3.5 h-3.5" />
          {commentCount ?? 0}
        </span>
      </div>

      {/* Mini vote breakdown bar */}
      {totalVotes > 0 && (
        <div
          className="flex gap-0.5 h-1.5 rounded-full overflow-hidden bg-muted"
          data-testid="proposal-card-mini-bar"
        >
          {yesVotes > 0 && (
            <div
              className="bg-green-500"
              style={{ width: `${(yesVotes / totalVotes) * 100}%`, minWidth: '2px' }}
            />
          )}
          {noVotes > 0 && (
            <div
              className="bg-red-500"
              style={{ width: `${(noVotes / totalVotes) * 100}%`, minWidth: '2px' }}
            />
          )}
          {abstainVotes > 0 && (
            <div
              className="bg-gray-400"
              style={{ width: `${(abstainVotes / totalVotes) * 100}%`, minWidth: '2px' }}
            />
          )}
          {needsRefinementVotes > 0 && (
            <div
              className="bg-yellow-500"
              style={{ width: `${(needsRefinementVotes / totalVotes) * 100}%`, minWidth: '2px' }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default ProposalCard;
